import React, { useState, useCallback } from 'react';
import {
    Box, Paper, Divider, Stack, Typography, TextField, Button, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import Swal from 'sweetalert2';
import { fetchConToken } from 'helpers/fetch';
import { getDataSession } from 'views/Utils/Common';
import Modal from 'components/modal/Modal';
import { useModal } from 'hooks/useModal';
import { currency } from 'resources/utilities';

const mapDetallesRecepcion = (compra) => (compra.vx_Compra_Detalles || []).map((detalle) => {
    const unidades = detalle.unidades ?? 0;
    const unidadesRecepcionadas = detalle.unidades_Rec_Dev ?? 0;
    const pendiente = unidades - unidadesRecepcionadas;
    return {
        id: detalle.id,
        clave_Articulo: detalle.clave_Variante || detalle.clave_Articulo,
        nombre_Articulo: detalle.nombre_Articulo || detalle.articulo_Nombre,
        unidades,
        unidadesRecepcionadas,
        pendiente,
        unidadesRecepcionar: pendiente > 0 ? pendiente : 0
    };
});

const procesarRespuestaRecepcion = (resp, onSuccess) => {
    if (resp?.hasOwnProperty('status') && resp.status === 400) {
        Object.entries(resp.errors).forEach(([, value]) => Swal.fire('Error', value[0], 'error'));
        return false;
    }
    if (resp?.hasOwnProperty('codigo') && resp.codigo !== 200) {
        Swal.fire('Error', resp.mensajes[0], 'error');
        return false;
    }
    Swal.fire('Exito', 'Se genero la recepcion correctamente', 'success');
    if (onSuccess) onSuccess();
    return true;
};

const CompraInfoHeader = ({ compra, proveedorNombre }) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <PersonIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>Información de la compra</Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
                <Typography variant="caption" color="text.secondary">Proveedor</Typography>
                <Typography variant="body1" fontWeight={500}>
                    {compra?.proveedor_Nombre || proveedorNombre || 'Sin proveedor'}
                </Typography>
            </Box>
            <Box>
                <Typography variant="caption" color="text.secondary">Folio</Typography>
                <Typography variant="body1">{compra?.folio || '—'}</Typography>
            </Box>
            <Box>
                <Typography variant="caption" color="text.secondary">Folio Prov.</Typography>
                <Typography variant="body1">{compra?.folio_Prov || '—'}</Typography>
            </Box>
            <Box>
                <Typography variant="caption" color="text.secondary">Importe neto</Typography>
                <Typography variant="body1" fontWeight={500}>{currency(compra?.importe_Neto ?? 0)}</Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                <Typography variant="caption" color="text.secondary">Descripción</Typography>
                <Typography variant="body1">{compra?.descripcion || '—'}</Typography>
            </Box>
        </Box>
    </Paper>
);

const RecepcionCompletaModal = ({ compra, onConfirmar, onCancelar, proveedorNombre }) => (
    <Box sx={{ minWidth: { xs: '100%', md: 640 } }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 3, py: 2, borderRadius: '4px 4px 0 0', mx: -1, mt: -1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Inventory2Icon />
                <Typography variant="h6" component="h2">Recepción completa</Typography>
            </Stack>
        </Box>

        <Box sx={{ px: 1, pt: 2 }}>
            <CompraInfoHeader compra={compra} proveedorNombre={proveedorNombre} />

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell><strong>Clave</strong></TableCell>
                            <TableCell><strong>Artículo</strong></TableCell>
                            <TableCell align="center"><strong>Unidades</strong></TableCell>
                            <TableCell align="center"><strong>Recepcionadas</strong></TableCell>
                            <TableCell align="center"><strong>Pendiente</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(compra?.vx_Compra_Detalles || []).map((detalle) => {
                            const pendiente = (detalle.unidades ?? 0) - (detalle.unidades_Rec_Dev ?? 0);
                            return (
                                <TableRow key={detalle.id} hover>
                                    <TableCell>{detalle.clave_Variante || detalle.clave_Articulo}</TableCell>
                                    <TableCell>{detalle.nombre_Articulo || detalle.articulo_Nombre}</TableCell>
                                    <TableCell align="center">{detalle.unidades}</TableCell>
                                    <TableCell align="center">{detalle.unidades_Rec_Dev ?? 0}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={pendiente}
                                            size="small"
                                            color={pendiente > 0 ? 'warning' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" color="inherit" onClick={onCancelar}>
                    Cancelar
                </Button>
                <Button variant="contained" color="primary" onClick={onConfirmar}>
                    Generar recepción
                </Button>
            </Stack>
        </Box>
    </Box>
);

const RecepcionParcialModal = ({
    compra,
    detallesRecepcion,
    onRecepcionChange,
    onConfirmar,
    onCancelar,
    totalUnidadesRecepcion,
    proveedorNombre,
}) => (
    <Box sx={{ minWidth: { xs: '100%', md: 720 } }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 3, py: 2, borderRadius: '4px 4px 0 0', mx: -1, mt: -1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Inventory2Icon />
                <Typography variant="h6" component="h2">Recepción parcial</Typography>
            </Stack>
        </Box>

        <Box sx={{ px: 1, pt: 2 }}>
            <CompraInfoHeader compra={compra} proveedorNombre={proveedorNombre} />

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell><strong>Clave</strong></TableCell>
                            <TableCell><strong>Artículo</strong></TableCell>
                            <TableCell align="center"><strong>Unidades</strong></TableCell>
                            <TableCell align="center"><strong>Recepcionadas</strong></TableCell>
                            <TableCell align="center" sx={{ minWidth: 140 }}><strong>A recepcionar</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {detallesRecepcion?.map((detalle) => (
                            <TableRow key={detalle.id} hover sx={detalle.pendiente <= 0 ? { opacity: 0.55 } : undefined}>
                                <TableCell>{detalle.clave_Articulo}</TableCell>
                                <TableCell>{detalle.nombre_Articulo}</TableCell>
                                <TableCell align="center">{detalle.unidades}</TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={detalle.unidadesRecepcionadas}
                                        size="small"
                                        color={detalle.unidadesRecepcionadas > 0 ? 'success' : 'default'}
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <TextField
                                        type="number"
                                        size="small"
                                        inputProps={{ min: 0, max: detalle.pendiente, step: 'any', style: { textAlign: 'center' } }}
                                        value={detalle.unidadesRecepcionar}
                                        disabled={detalle.pendiente <= 0}
                                        onChange={({ target }) => onRecepcionChange(detalle.id, target.value)}
                                        sx={{ width: 100 }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
                <Typography variant="body2" color="text.secondary">
                    Total a recepcionar: <strong>{totalUnidadesRecepcion}</strong> unidad(es)
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" color="inherit" onClick={onCancelar}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="primary" onClick={onConfirmar} disabled={totalUnidadesRecepcion <= 0}>
                        Generar recepción
                    </Button>
                </Stack>
            </Stack>
        </Box>
    </Box>
);

const useRecepcionCompra = ({ onSuccess, proveedorNombre } = {}) => {
    const [isOpenCompleta, openCompleta, closeCompleta] = useModal(false);
    const [isOpenParcial, openParcial, closeParcial] = useModal(false);
    const [compraRecepcion, setCompraRecepcion] = useState(null);
    const [detallesRecepcion, setDetallesRecepcion] = useState([]);

    const cerrarModales = useCallback(() => {
        closeCompleta();
        closeParcial();
        setCompraRecepcion(null);
        setDetallesRecepcion([]);
    }, [closeCompleta, closeParcial]);

    const cargarCompra = useCallback((compra_id) => {
        return fetchConToken(`VxCompras?id=${compra_id}&sucursal_Id=${getDataSession('sucursal')}`).then((compra) => {
            if (compra?.hasOwnProperty('status') && compra.status === 400) {
                Object.entries(compra.errors).forEach(([, value]) => Swal.fire('Error', value[0], 'error'));
                return null;
            }
            if (compra?.hasOwnProperty('codigo') && compra.codigo !== 200) {
                Swal.fire('Error', compra.mensajes[0], 'error');
                return null;
            }
            return compra;
        });
    }, []);

    const ejecutarRecepcionTotal = useCallback((compra) => {
        const payload = {
            documento_Id: compra.id,
            sucursal_Id: compra.sucursal_Id ?? getDataSession('sucursal'),
            recepcion_Total: true,
            detalles: []
        };

        fetchConToken('VxCompras/GenerarRecepcion', payload, 'POST').then((resp) => {
            procesarRespuestaRecepcion(resp, () => {
                cerrarModales();
                onSuccess?.();
            });
        }).catch((error) => console.error('Error al generar recepcion:', error));
    }, [cerrarModales, onSuccess]);

    const abrirRecepcion = useCallback((compra_id) => {
        Swal.fire({
            title: 'Recepción de compra',
            html: `
                <p style="margin:0 0 12px">¿Cómo desea recepcionar esta compra?</p>
                <p style="margin:0;font-size:13px;color:#666;text-align:left">
                    <strong>Recepción completa:</strong> genera la recepción de todas las unidades pendientes.<br/>
                    <strong>Recepción parcial:</strong> elige la cantidad a recepcionar por artículo.
                </p>
            `,
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Recepción completa',
            denyButtonText: 'Recepción parcial',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#1976d2',
            denyButtonColor: '#ed6c02',
        }).then((result) => {
            if (!result.isConfirmed && !result.isDenied) return;

            cargarCompra(compra_id).then((compra) => {
                if (!compra) return;

                const pendientes = (compra.vx_Compra_Detalles || []).some(
                    (d) => ((d.unidades ?? 0) - (d.unidades_Rec_Dev ?? 0)) > 0
                );
                if (!pendientes) {
                    Swal.fire('Info', 'No hay unidades pendientes por recepcionar', 'info');
                    return;
                }

                setCompraRecepcion(compra);
                if (result.isConfirmed) {
                    openCompleta();
                    return;
                }

                setDetallesRecepcion(mapDetallesRecepcion(compra));
                openParcial();
            }).catch((error) => console.error('Error al obtener la compra:', error));
        });
    }, [cargarCompra, openCompleta, openParcial]);

    const handleRecepcionChange = useCallback((detalleId, value) => {
        setDetallesRecepcion((prev) => prev.map((detalle) => {
            if (detalle.id !== detalleId) return detalle;

            let unidadesRecepcionar = parseFloat(value);
            if (isNaN(unidadesRecepcionar) || unidadesRecepcionar < 0) unidadesRecepcionar = 0;
            if (unidadesRecepcionar > detalle.pendiente) unidadesRecepcionar = detalle.pendiente;

            return { ...detalle, unidadesRecepcionar };
        }));
    }, []);

    const confirmarRecepcionParcial = useCallback(() => {
        const detallesEnviar = detallesRecepcion.filter((x) => x.unidadesRecepcionar > 0);
        if (detallesEnviar.length === 0) {
            Swal.fire('Info', 'Debe indicar al menos una unidad a recepcionar', 'info');
            return;
        }

        const totalUnidades = detallesEnviar.reduce((acc, d) => acc + d.unidadesRecepcionar, 0);

        Swal.fire({
            title: 'Generar recepción',
            text: `¿Desea recepcionar ${totalUnidades} unidad(es)?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, generar recepción',
            cancelButtonText: 'No'
        }).then((result) => {
            if (!result.isConfirmed) return;

            const payload = {
                documento_Id: compraRecepcion.id,
                sucursal_Id: compraRecepcion.sucursal_Id ?? getDataSession('sucursal'),
                recepcion_Total: false,
                detalles: detallesEnviar.map((x) => ({
                    detalle_Compra_Id: x.id,
                    unidades_Recepcionar: x.unidadesRecepcionar
                }))
            };

            fetchConToken('VxCompras/GenerarRecepcion', payload, 'POST').then((resp) => {
                procesarRespuestaRecepcion(resp, () => {
                    cerrarModales();
                    onSuccess?.();
                });
            }).catch((error) => console.error('Error al generar recepcion:', error));
        });
    }, [detallesRecepcion, compraRecepcion, cerrarModales, onSuccess]);

    const confirmarRecepcionCompleta = useCallback(() => {
        Swal.fire({
            title: 'Generar recepción',
            text: '¿Desea generar la recepción completa?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, generar recepción',
            cancelButtonText: 'No'
        }).then((result) => {
            if (!result.isConfirmed) return;
            ejecutarRecepcionTotal(compraRecepcion);
        });
    }, [compraRecepcion, ejecutarRecepcionTotal]);

    const totalUnidadesRecepcion = detallesRecepcion.reduce((acc, d) => acc + (d.unidadesRecepcionar || 0), 0);

    const RecepcionCompraPortal = () => (
        <>
            <Modal isOpen={isOpenCompleta} closeModal={cerrarModales} min>
                <RecepcionCompletaModal
                    compra={compraRecepcion}
                    onConfirmar={confirmarRecepcionCompleta}
                    onCancelar={cerrarModales}
                    proveedorNombre={proveedorNombre}
                />
            </Modal>
            <Modal isOpen={isOpenParcial} closeModal={cerrarModales} min>
                <RecepcionParcialModal
                    compra={compraRecepcion}
                    detallesRecepcion={detallesRecepcion}
                    onRecepcionChange={handleRecepcionChange}
                    onConfirmar={confirmarRecepcionParcial}
                    onCancelar={cerrarModales}
                    totalUnidadesRecepcion={totalUnidadesRecepcion}
                    proveedorNombre={proveedorNombre}
                />
            </Modal>
        </>
    );

    return { abrirRecepcion, RecepcionCompraPortal };
};

export { useRecepcionCompra };
